/* IMPORTANT: Before modifying this file, please update CHANGELOG.md with a summary of your changes.
Also, make clear comments about every change in this file and what it was replacing so that we don't end up trying the same fixes repeatedly. */

import React, { useState } from 'react';

interface FormData {
  apiName: string;
  useCase: string;
  name: string;
  email: string;
  acceptTerms: boolean;
}

interface FormErrors {
  apiName?: string;
  useCase?: string;
  name?: string;
  email?: string;
  acceptTerms?: string;
}

interface FormProps {
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
}

const Form: React.FC<FormProps> = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState<FormData>({
    apiName: '',
    useCase: '',
    name: '',
    email: '',
    acceptTerms: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.apiName.trim()) {
      newErrors.apiName = 'API name is required';
    }
    
    if (!formData.useCase.trim()) {
      newErrors.useCase = 'Use case is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">API Integration Request</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="apiName" className="block text-gray-700 font-medium mb-2">
            API Name/Description
          </label>
          <input
            type="text"
            id="apiName"
            name="apiName"
            value={formData.apiName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${
              errors.apiName ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="e.g., Twitter API, Stripe Payment Processing"
          />
          {errors.apiName && (
            <p className="text-red-500 text-sm mt-1">{errors.apiName}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="useCase" className="block text-gray-700 font-medium mb-2">
            Use Case/Reason
          </label>
          <textarea
            id="useCase"
            name="useCase"
            value={formData.useCase}
            onChange={handleChange}
            rows={3}
            className={`w-full px-3 py-2 border ${
              errors.useCase ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Describe how you plan to use this API integration"
          />
          {errors.useCase && (
            <p className="text-red-500 text-sm mt-1">{errors.useCase}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="John Doe"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
            Your Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        <div className="mb-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                  errors.acceptTerms ? 'border-red-500' : ''
                }`}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="acceptTerms" className="text-gray-600">
                I agree to receive documentation and resources related to this API integration
              </label>
              {errors.acceptTerms && (
                <p className="text-red-500 text-sm mt-1">{errors.acceptTerms}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Processing...' : 'Generate Integration Prompt'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form;